import type { PropsWithChildren } from 'react';
import Head from 'next/head';
import { useSiteInfo } from '~/pages/_shared';

export type PageTitleProps = PropsWithChildren<{
   includeSiteName?: boolean;
}>;

/**
 * @param children The translated title of the page
 * @param includeSiteName Whether to include a subtitle with the site name. Defaults to `true`.
 *
 * If you are replacing an existing page from the PHP monolith, you can find the existing site title
 * by looking for a call to `$page->setTitle()`. For example:
 *    ```php
 *    $page->setTitle(___('Single Sign On Verification', false));
 *    ```
 *    is equivalent to...
 *    ```tsx
 *    <PageTitle includeSiteName={false}>
 *       {_js('Single Sign On Verification')}
 *    </PageTitle>
 *    ```
 */
export function PageTitle({ children, includeSiteName = true }: PageTitleProps) {
   const { data: siteInfo } = useSiteInfo();
   const siteName = siteInfo?.title || siteInfo?.name;

   let titleMessage = children;
   if (!children || typeof children !== 'string') {
      titleMessage = siteName;
   } else if (includeSiteName && siteName) {
      titleMessage += ` - ${siteName}`;
   }

   return (
      <Head>
         <title>{titleMessage}</title>
      </Head>
   );
}
